import {
  fetchSlaInstances,
  fetchSlaPreferences,
  fetchSlaSettings,
  isSlaFixtureMode,
  patchSlaPreferences,
  toSlaApiError,
} from "api";
import { useUser } from "hooks";
import {
  SLA_ACTIVITIES_INSTANCES_PAGE,
  SLA_ACTIVITIES_STALE_AFTER_SECONDS,
  isSlaActivityStale,
  slaActivityBanner,
  slaActivityCountdown,
  slaActivityDetail,
  slaActivityForTask,
  slaActivityOldestAsOf,
  slaActivityPreferencePatch,
  slaActivityPreferencesScope,
  slaProcessInstanceFor,
  slaActivityThresholdCrossings,
  SLA_ACTIVITY_DISMISS_PATCH,
  type SlaActivitiesState,
  type SlaActivityBannerView,
  type SlaActivityCountdownView,
  type SlaActivityDetailView,
  type SlaActivityPreferenceKey,
  type SlaActivityTaskIdentity,
} from "model/slaActivitiesModel";
import { SLA_INSTANCES_PAGE_SIZE, SLA_TARGET_ACTIVITY } from "model/slaModel";
import type { SlaInstance, SlaUserPreferencesDto } from "model/slaModel";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { readCachedEnabled, writeCachedEnabled } from "./SlaLiveKpiChips";

/**
 * Stato condiviso della superficie **Attivita' SLA** (F3c). E' il solo punto che
 * parla con l'API: i consumatori (badge sulle righe ToDo, banner, dettaglio)
 * leggono da qui, cosi' non nasce una chiamata per riga (sez. 3: "nessun N+1 per
 * caricare lo SLA di ogni riga").
 *
 * Cosa fa e perche':
 * - legge le istanze aperte dell'organizzazione (`fetchSlaInstances`, prima
 *   pagina: limite dichiarato in `SLA_ACTIVITIES_INSTANCES_LIMIT`) e le preferenze
 *   dell'utente autenticato (`fetchSlaPreferences`, parametro `idorganization`
 *   esplicito perche' un utente puo' stare in piu' organizzazioni);
 * - tiene un **orologio locale** che avanza di un secondo: serve solo a misurare
 *   il tempo trascorso da `as_of` (istante del Server) per il countdown
 *   lavorativo. Nessun calendario e nessun giorno solare nel browser (sez. 1);
 * - **refresh a rientro sul tab** (`visibilitychange`, `focus`, `pageshow`) e a
 *   **soglia** (`next_clock_change_at` superato, cioe' pausa/ripresa del clock):
 *   e' la politica di refresh richiesta da F3d, e non resetta nulla di locale —
 *   la pagina attivita' non perde lo stato che ha gia' (sez. 5 F3c/F3d);
 * - **dismiss per utente**: il banner si chiude con
 *   `PATCH /sla/preferences` (`greeting_dismissed`), rotta dell'utente
 *   autenticato (nessun `iduser`: lo identifica il JWT). La preferenza resta
 *   dell'utente, non del dispositivo.
 *
 * Errori: un 403 sulle preferenze non e' un errore fatale (la lettura delle
 * istanze resta valida) e viene dichiarato; un errore sulle istanze spegne lo
 * stato SLA invece di lasciarlo verde (sez. 5 F3d).
 */
export interface SlaActivityInstanceView {
  instance: SlaInstance;
  /** Vista del dettaglio: originale/vigente/override (residuo sul motivo). */
  detail: SlaActivityDetailView;
  countdown: SlaActivityCountdownView;
}

export interface SlaActivitiesView extends SlaActivityInstanceView {
  /**
   * Istanza **di processo** dello stesso processo quando la vista principale e'
   * uno SLA di attivita': la promessa del processo resta visibile come dato
   * secondario. `null` quando la principale e' gia' quella di processo o quando
   * il processo non ha SLA proprio.
   */
  process: SlaActivityInstanceView | null;
}

export interface SlaActivitiesContextValue {
  state: SlaActivitiesState;
  /** Orologio locale (ms): solo per misurare il trascorso da `as_of`. */
  nowMs: number;
  /** Istanza che governa un task della lista attivita', `null` se nessuna. */
  viewForTask: (task: SlaActivityTaskIdentity) => SlaActivitiesView | null;
  /** Istanza di processo (`target_type = 1`) di un processo, `null` se nessuna. */
  processViewFor: (idprocess: number) => SlaActivityInstanceView | null;
  /** Riga del banner (payload), `null` se nessuna istanza a rischio o violata. */
  banner: SlaActivityBannerView | null;
  /** true quando il banner va mostrato: c'e' una riga e il saluto non e' chiuso. */
  bannerVisible: boolean;
  bannerDismissed: boolean;
  /** Chiude il saluto per l'utente autenticato (PATCH per utente+organizzazione). */
  dismissBanner: () => Promise<void>;
  preferences: SlaUserPreferencesDto | null;
  preferencesForbidden: boolean;
  setPreference: (key: SlaActivityPreferenceKey, value: boolean) => Promise<void>;
  /** Rilettura esplicita (rientro sul tab, soglia del clock, azione utente). */
  refresh: () => void;
  /**
   * Ambito tecnico della preferenza, `user:<id>@organization:<id>`. Non e' una
   * label: la superficie lo espone solo come attributo `data-scope`.
   */
  preferencesScope: string | null;
  /**
   * true quando l'organizzazione **usa lo SLA**: almeno una lettura delle
   * istanze di questa organizzazione ha restituito righe. Il Server non espone
   * ai non-admin un segnale di organizzazione (le impostazioni sono admin-only,
   * le definizioni sono per modello), quindi il segnale disponibile e' la
   * presenza di istanze aperte. Resta vero per la sessione della pagina anche se
   * le istanze poi scendono a zero (stato vuoto reso), torna falso al cambio di
   * organizzazione. Finche' e' falso (caricamento, errore, nessuna istanza) la
   * strip non si rende.
   */
  slaInUse: boolean;
  /**
   * true quando le impostazioni SLA dell'organizzazione dichiarano `enabled`:
   * lo stesso segnale con cui il Monitor (`SlaLiveKpiChips`) mostra i chip KPI.
   * La rotta e' admin-only: per un non-admin (403) resta falso e vale solo
   * `slaInUse`. Parte dall'ultimo esito noto in cache (stessa chiave del
   * Monitor) e la lettura lo corregge.
   */
  slaEnabled: boolean;
  locale: string;
}

const SlaActivitiesContext = createContext<SlaActivitiesContextValue | null>(null);

/**
 * Consumo **non obbligatorio**: la pagina attivita' e' usata anche fuori dal
 * provider (storico, viste embedded). Senza provider i consumatori rendono nulla
 * invece di rompere il rendering esistente.
 */
export const useSlaActivities = (): SlaActivitiesContextValue | null =>
  useContext(SlaActivitiesContext);

/** Vista SLA di un task, `null` quando il task non ha SLA (o non c'e' provider). */
export const useSlaActivityForTask = (
  task: SlaActivityTaskIdentity | null | undefined,
): SlaActivitiesView | null => {
  const context = useSlaActivities();
  if (!context || !task) return null;
  return context.viewForTask(task);
};

/** Task visibile nella lista: identita' SLA piu' il token, se noto. */
export interface SlaActivityVisibleTask extends SlaActivityTaskIdentity {
  idprocesstoken?: number;
}

/** Attesa prima della rilettura per task nuovi: assorbe le raffiche di SSE. */
const SLA_NEW_TASKS_DEBOUNCE_MS = 400;

const slaVisibleTaskKey = (task: SlaActivityVisibleTask): string =>
  task.idprocesstoken !== undefined
    ? `t:${task.idprocesstoken}`
    : `p:${task.idprocess}:${task.designerid ?? ""}`;

interface SlaActivitiesProviderProps {
  idorganization: number;
  /**
   * Task oggi in lista (ToDo + Doing). La lista si aggiorna via SSE
   * (`useActivities`) quando nasce un processo o arriva un task: il provider
   * rilegge le istanze quando compaiono token mai visti, cosi' il chip SLA
   * appare insieme al task senza ricaricare la pagina.
   */
  tasks?: SlaActivityVisibleTask[];
  /**
   * Pagine di istanze da leggere (default 1 = solo la prima, il limite
   * dichiarato in `SLA_ACTIVITIES_INSTANCES_LIMIT`). Il Server non filtra per
   * processo e ordina per scadenza: con piu' di una pagina di istanze aperte i
   * processi piu' recenti (in tempo) restano fuori dalla prima. La lista viva
   * chiede piu' pagine, con un tetto, per unire ogni riga alla sua istanza.
   */
  maxPages?: number;
  children: React.ReactNode;
}

export const SlaActivitiesProvider: React.FC<SlaActivitiesProviderProps> = ({
  idorganization,
  tasks,
  maxPages = 1,
  children,
}) => {
  const user = useUser();
  const locale = user?.i18n || navigator.language.split("-")[0] || "en";

  const [instances, setInstances] = useState<SlaInstance[]>([]);
  const [preferences, setPreferences] = useState<SlaUserPreferencesDto | null>(null);
  const [preferencesForbidden, setPreferencesForbidden] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  /** Organizzazione per cui si e' gia' vista almeno un'istanza (vedi `slaInUse`). */
  const [slaInUseOrganization, setSlaInUseOrganization] = useState<number | null>(null);
  /** Organizzazione con SLA attivo nelle impostazioni (vedi `slaEnabled`). */
  const [slaEnabledOrganization, setSlaEnabledOrganization] = useState<number | null>(() =>
    readCachedEnabled(idorganization) ? idorganization : null,
  );

  const mountedRef = useRef<boolean>(true);

  /**
   * Una sola lettura in volo: le richieste che arrivano mentre una e' in corso
   * (focus + visibilitychange + pageshow, SSE ravvicinati) si fondono in UNA
   * rilettura successiva, cosi' l'ultimo evento non va perso e non nasce una
   * raffica di GET.
   */
  const inFlightRef = useRef<boolean>(false);
  const queuedRef = useRef<boolean>(false);

  const load = useCallback(async () => {
    if (inFlightRef.current) {
      queuedRef.current = true;
      return;
    }
    inFlightRef.current = true;
    setLoading(true);
    try {
      // Pagine in sequenza fino a una pagina corta (o senza istanze nuove, es.
      // fixture) o al tetto `maxPages`: nessuna lettura illimitata.
      const byId = new Map<number, SlaInstance>();
      const pages = Math.max(1, Math.floor(maxPages));
      for (let offset = 0; offset < pages; offset += 1) {
        const response = await fetchSlaInstances(idorganization, {
          page: SLA_ACTIVITIES_INSTANCES_PAGE + offset,
        });
        if (!mountedRef.current) return;
        const pageInstances = response?.instances ?? [];
        const before = byId.size;
        pageInstances.forEach((instance) => byId.set(instance.id, instance));
        if (pageInstances.length < SLA_INSTANCES_PAGE_SIZE || byId.size === before) break;
      }
      setInstances(Array.from(byId.values()));
      if (byId.size > 0) setSlaInUseOrganization(idorganization);
      setError(null);
      setErrorStatus(null);
    } catch (caught) {
      if (!mountedRef.current) return;
      const apiError = toSlaApiError(caught);
      setInstances([]);
      setError(apiError.message || "sla.activities.load_error");
      setErrorStatus(apiError.status);
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
    if (queuedRef.current && mountedRef.current) {
      queuedRef.current = false;
      void load();
    }
  }, [idorganization, maxPages]);

  const loadPreferences = useCallback(async () => {
    try {
      const response = await fetchSlaPreferences(idorganization);
      if (!mountedRef.current) return;
      setPreferences(response);
      setPreferencesForbidden(false);
    } catch (caught) {
      if (!mountedRef.current) return;
      const error = toSlaApiError(caught);
      setPreferences(null);
      setPreferencesForbidden(error.status === 403 || error.status === 401);
    }
  }, [idorganization]);

  /**
   * Impostazioni SLA: una lettura per organizzazione, come il Monitor. Errore o
   * 403 (non admin) non toccano la cache del Monitor: si resta su `slaInUse`.
   */
  useEffect(() => {
    let cancelled = false;
    setSlaEnabledOrganization(readCachedEnabled(idorganization) ? idorganization : null);
    (async () => {
      try {
        const settings = await fetchSlaSettings(idorganization);
        if (cancelled) return;
        const isEnabled = settings?.enabled === true;
        writeCachedEnabled(idorganization, isEnabled);
        setSlaEnabledOrganization(isEnabled ? idorganization : null);
      } catch {
        if (cancelled) return;
        setSlaEnabledOrganization(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [idorganization]);

  useEffect(() => {
    mountedRef.current = true;
    void load();
    void loadPreferences();
    return () => {
      mountedRef.current = false;
    };
  }, [load, loadPreferences]);

  /**
   * Orologio locale: 1 secondo. Misura **solo** il tempo trascorso da `as_of`; non
   * rilegge il calendario e non tocca i payload.
   */
  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  /**
   * Refresh a rientro sul tab: `visibilitychange` quando la pagina torna visibile,
   * `focus` e `pageshow`. Non azzera lo stato: la lista ToDo resta quella gia'
   * resa mentre la rilettura e' in corso (sez. 5 F3c: "Ritorno ToDo non resetta").
   */
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") void load();
    };
    const onFocus = () => void load();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    window.addEventListener("pageshow", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pageshow", onFocus);
    };
  }, [load]);

  /**
   * Task nuovi in lista: la lista ToDo/Doing si aggiorna via SSE, le istanze SLA
   * no. Quando compaiono token mai visti si rilegge una volta (debounce), perche'
   * il Server crea l'istanza di processo nella stessa transazione dell'avvio e
   * quelle di attivita' quando il token va in ToDo. Ogni token conta una sola
   * volta: un task senza SLA non produce cicli di refetch. Il primo lotto
   * (apertura pagina) rilegge solo se contiene task senza istanza nota: la
   * lettura al mount copre gli altri, ma se la lista partiva vuota il primo task
   * arrivato e' proprio quello appena avviato.
   */
  const seenTasksRef = useRef<Set<string> | null>(null);
  const newTasksTimerRef = useRef<number | null>(null);
  const instancesRef = useRef<SlaInstance[]>(instances);
  instancesRef.current = instances;
  useEffect(() => {
    seenTasksRef.current = null;
  }, [idorganization]);
  useEffect(() => {
    if (!tasks || tasks.length === 0) return;
    const firstBatch = seenTasksRef.current === null;
    const seen = seenTasksRef.current ?? new Set<string>();
    seenTasksRef.current = seen;
    const fresh = tasks.filter((task) => !seen.has(slaVisibleTaskKey(task)));
    if (fresh.length === 0) return;
    fresh.forEach((task) => seen.add(slaVisibleTaskKey(task)));
    if (
      firstBatch &&
      fresh.every((task) => slaActivityForTask(instancesRef.current, task) !== null)
    ) {
      return;
    }
    if (newTasksTimerRef.current !== null) window.clearTimeout(newTasksTimerRef.current);
    newTasksTimerRef.current = window.setTimeout(() => {
      newTasksTimerRef.current = null;
      void load();
    }, SLA_NEW_TASKS_DEBOUNCE_MS);
  }, [tasks, load]);
  useEffect(
    () => () => {
      if (newTasksTimerRef.current !== null) window.clearTimeout(newTasksTimerRef.current);
    },
    [],
  );

  const countdowns = useMemo(
    () => instances.map((instance) => slaActivityCountdown(instance, nowMs)),
    [instances, nowMs],
  );

  /**
   * Soglia: quando il browser supera un `next_clock_change_at` dichiarato, il
   * payload non descrive piu' il clock (pausa o ripresa): si richiede al Server
   * invece di dedurre l'apertura del calendario nel browser (sez. 5 F3d).
   */
  const needsRefetch = countdowns.some((countdown) => countdown.needsRefetch);
  useEffect(() => {
    if (needsRefetch) void load();
  }, [needsRefetch, load]);

  /**
   * Soglia di salute: quando il countdown locale (clock in corsa) attraversa la
   * scadenza o `risk_at`, la salute del payload e' superata e si rilegge dal
   * Server. Una sola rilettura per istanza e per attraversamento: le chiavi gia'
   * servite restano in `firedCrossingsRef`, cosi' un payload invariato non
   * produce un ciclo di refetch.
   */
  const firedCrossingsRef = useRef<Set<string>>(new Set());
  const pendingCrossing = useMemo(
    () =>
      instances
        .flatMap((instance) => slaActivityThresholdCrossings(instance, nowMs))
        .filter((key) => !firedCrossingsRef.current.has(key)),
    [instances, nowMs],
  );
  useEffect(() => {
    if (pendingCrossing.length === 0) return;
    pendingCrossing.forEach((key) => firedCrossingsRef.current.add(key));
    void load();
  }, [pendingCrossing, load]);

  const asOf = useMemo(
    () => slaActivityOldestAsOf(instances.map((instance) => instance.as_of)),
    [instances],
  );

  const instanceView = useCallback(
    (instance: SlaInstance): SlaActivityInstanceView => ({
      instance,
      detail: slaActivityDetail(instance, nowMs),
      countdown: slaActivityCountdown(instance, nowMs),
    }),
    [nowMs],
  );

  const processViewFor = useCallback(
    (idprocess: number): SlaActivityInstanceView | null => {
      const instance = slaProcessInstanceFor(instances, idprocess);
      return instance ? instanceView(instance) : null;
    },
    [instances, instanceView],
  );

  const viewForTask = useCallback(
    (task: SlaActivityTaskIdentity): SlaActivitiesView | null => {
      const instance = slaActivityForTask(instances, task);
      if (!instance) return null;
      const process =
        instance.target_type === SLA_TARGET_ACTIVITY ? processViewFor(instance.idprocess) : null;
      return { ...instanceView(instance), process };
    },
    [instances, instanceView, processViewFor],
  );

  const banner = useMemo(
    () => slaActivityBanner(instances, nowMs),
    [instances, nowMs],
  );

  const dismissBanner = useCallback(async () => {
    await patchSlaPreferences(idorganization, SLA_ACTIVITY_DISMISS_PATCH);
    if (!mountedRef.current) return;
    setPreferences((current) =>
      current
        ? { ...current, greetingdismissedat: new Date().toISOString() }
        : current,
    );
  }, [idorganization]);

  const setPreference = useCallback(
    async (key: SlaActivityPreferenceKey, value: boolean) => {
      await patchSlaPreferences(idorganization, slaActivityPreferencePatch(key, value));
      if (!mountedRef.current) return;
      setPreferences((current) => {
        if (!current) return current;
        switch (key) {
          case "digest":
            return { ...current, digestenabled: value };
          case "escalation":
            return { ...current, escalationenabled: value };
          default:
            return { ...current, transactionalenabled: value };
        }
      });
    },
    [idorganization],
  );

  /**
   * Rilettura esplicita, stesso percorso del rientro sul tab: nessuno stato locale
   * viene azzerato, quindi le righe ToDo non "tornano indietro" (sez. 5 F3c).
   */
  const refresh = useCallback(() => {
    void load();
  }, [load]);

  const bannerDismissed = Boolean(preferences?.greetingdismissedat);

  const value = useMemo<SlaActivitiesContextValue>(
    () => ({
      state: {
        instances,
        preferences,
        loading,
        error,
        forbidden: errorStatus === 403 || errorStatus === 401,
        asOf,
        stale: isSlaActivityStale(
          asOf,
          nowMs,
          SLA_ACTIVITIES_STALE_AFTER_SECONDS,
        ),
        fixtures: isSlaFixtureMode(),
      },
      nowMs,
      viewForTask,
      processViewFor,
      banner,
      bannerVisible: Boolean(banner) && !bannerDismissed,
      bannerDismissed,
      dismissBanner,
      preferences,
      preferencesForbidden,
      setPreference,
      refresh,
      preferencesScope: preferences ? slaActivityPreferencesScope(preferences) : null,
      slaInUse: slaInUseOrganization === idorganization,
      slaEnabled: slaEnabledOrganization === idorganization,
      locale,
    }),
    [
      instances,
      preferences,
      loading,
      error,
      errorStatus,
      asOf,
      nowMs,
      viewForTask,
      processViewFor,
      banner,
      bannerDismissed,
      dismissBanner,
      preferencesForbidden,
      setPreference,
      refresh,
      slaInUseOrganization,
      slaEnabledOrganization,
      idorganization,
      locale,
    ],
  );

  return (
    <SlaActivitiesContext.Provider value={value}>
      {children}
    </SlaActivitiesContext.Provider>
  );
};

export default SlaActivitiesProvider;
